import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ClipboardList, Download, Flag, PanelRightClose, Plus } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/Table';
import { Badge } from '../ui/Badge';
import { TriangleUp, TriangleDown, TriangleBoth } from '../ui/SortIcons';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/DropdownMenu';
import { cn } from '../../lib/utils';
import { formatSlashDate } from '../../utils/dateUtils';
import { isSmartLightEmployee } from '../../authUtils';
import {
  fetchEngagementFilterOptions,
  fetchEngagementReferrals,
} from '../../services/carrierEngagementService';
import {
  useCarrierEngagementApiFilters,
  useCarrierEngagementFilters,
} from '../../contexts/CarrierEngagementFilterContext';
import { EngagementReferralDrawer } from './EngagementReferralDrawer';
import { MedicalRecordsDrawer } from './MedicalRecordsDrawer';
import { Tooltip } from '../ui/Tooltip';
import { SlDispositionModal } from '../referral-drawer/SlDispositionModal';
import { ColumnFilterDropdown } from '../referrals/ColumnFilterDropdown';

// Aging Out is a computed Yes/No flag with no server-provided value-set, so
// its dropdown options are fixed client-side. Every other filterable column
// sources its options from /api/v1/referrals/filter-options/ (optionsKey).
const AGING_OUT_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

const EMPTY_FILTER_OPTIONS = {};

// Cells that open the SL-disposition modal when the viewer is an SL employee.
const SL_EDITABLE_COLUMNS = new Set(['sl_response', 'sl_reason', 'sl_action', 'latest_comment']);

// Column shape mirrors CP's REFERRAL_COLUMNS for visual parity:
//   { key, label, displayKey?, render?, truncate?, textSize?, className? }
// `displayKey` is the field name on the API row; falls back to `key`.
//
// `sortable: true` adds the sort affordance (same icons/menu as the Carrier
// Portal table) and sends the column key as an `ordering` field. The backend
// sorts the FULL dataset, so the order reflects every matching referral, not
// just the rows on the page. c_score / realized / leakage live only on the
// legacy view, so the backend sorts them over the full dataset in Python and
// returns the first page (no cursor) — which is exactly what this table
// renders. Three columns remain NOT sortable: referral_scheme (also legacy-only
// but not yet wired into that path, pending PRODUCT-276); latest_comment (an
// unbounded TEXT column SQL Server can't ORDER BY — sort by last_comment_date
// instead); and bucket (no real backing data). The backend returns 400 if any
// of those three are requested as a sort field.
const OVERVIEW_COLUMNS = [
  { key: 'client', label: 'Client', sortable: true, filterable: true, filterKey: 'client_id', optionsKey: 'clients', textSize: 'text-sm', className: 'whitespace-nowrap' },
  { key: 'tpa_carrier', label: 'TPA/Carrier', sortable: true, filterable: true, filterKey: 'partner_id', optionsKey: 'tpa_carriers', textSize: 'text-sm', className: 'whitespace-nowrap' },
  {
    key: 'referral_type',
    label: 'Referral Type',
    sortable: true,
    filterable: true,
    filterKey: 'referral_type',
    optionsKey: 'referral_types',
    render: (val) =>
      val ? (
        <Badge variant="outline" className="rounded px-2 py-0 font-medium whitespace-nowrap">
          {val}
        </Badge>
      ) : '',
  },
  { key: 'bucket', label: 'Bucket', className: 'whitespace-nowrap' },
  { key: 'referral_scheme', label: 'Referral Scheme', filterable: true, filterKey: 'referral_scheme', optionsKey: 'referral_schemes', className: 'whitespace-nowrap' },
  {
    key: 'publish_dt',
    label: 'Publish Date',
    sortable: true,
    className: 'text-muted-foreground whitespace-nowrap',
    render: (val) => (val ? formatSlashDate(val) : ''),
  },
  {
    key: 'aging_out',
    label: 'Aging Out',
    sortable: true,
    filterable: true,
    filterKey: 'aging_out',
    staticOptions: AGING_OUT_OPTIONS,
    render: (val) => (val ? <Flag className="h-3.5 w-3.5 text-destructive mx-auto" /> : ''),
  },
  { key: 'referral_id', label: 'Referral ID', sortable: true, textSize: 'text-sm', className: 'font-medium whitespace-nowrap' },
  { key: 'c_score', label: 'C Score', sortable: true, filterable: true, filterKey: 'c_score', optionsKey: 'c_scores', className: 'tabular-nums text-center whitespace-nowrap' },
  {
    key: 'referral_name',
    label: 'Referral Name',
    sortable: true,
    truncate: true,
    className: 'max-w-[180px]',
  },
  { key: 'identified', label: 'Identified', sortable: true, className: 'tabular-nums text-right whitespace-nowrap' },
  { key: 'realized', label: 'Realized', sortable: true, className: 'tabular-nums text-right whitespace-nowrap' },
  { key: 'leakage', label: 'Leakage', sortable: true, className: 'tabular-nums text-right whitespace-nowrap' },
  { key: 'base_status', label: 'Base Status', sortable: true, filterable: true, filterKey: 'status', optionsKey: 'base_statuses', className: 'whitespace-nowrap' },
  { key: 'tpa_carrier_status', label: 'TPA/Carrier Status', sortable: true, filterable: true, filterKey: 'tpa_carrier_status', optionsKey: 'tpa_carrier_statuses', className: 'whitespace-nowrap' },
  {
    key: 'tpa_carrier_reason',
    label: 'TPA/Carrier Reason',
    sortable: true,
    filterable: true,
    filterKey: 'tpa_carrier_reason',
    optionsKey: 'tpa_carrier_reasons',
    truncate: true,
    className: 'max-w-[130px] text-muted-foreground',
  },
  { key: 'sl_response', label: 'SL Response', sortable: true, filterable: true, filterKey: 'sl_response', optionsKey: 'sl_responses', className: 'whitespace-nowrap' },
  { key: 'sl_reason', label: 'SL Reason', sortable: true, filterable: true, filterKey: 'sl_reason', optionsKey: 'sl_reasons', truncate: true, className: 'max-w-[130px] text-muted-foreground' },
  { key: 'sl_action', label: 'SL Action', sortable: true, filterable: true, filterKey: 'sl_action', optionsKey: 'sl_actions', className: 'whitespace-nowrap' },
  {
    key: 'last_comment_date',
    label: 'Last Comment Date',
    sortable: true,
    className: 'text-muted-foreground whitespace-nowrap',
    render: (val) => (val ? formatSlashDate(val) : ''),
  },
  {
    key: 'latest_comment',
    label: 'Last Comment',
    truncate: true,
    className: 'max-w-[180px] text-muted-foreground',
    render: (val, row) =>
      row?.has_new_comment ? (
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
          <span className="truncate">{val ?? ''}</span>
        </span>
      ) : (val ?? ''),
  },
];

// Impact dots: two rows of three. Top row reflects one metric (blue, or red
// when flagged); bottom row a second metric (black). Unfilled dots are muted.
// No backing API field exists yet — values are static placeholders cycled by
// row index so the column previews the range of states. Wire `top`/`flagged`/
// `bottom` to real referral data once the field lands.
const IMPACT_DOT_SAMPLES = [
  { top: 0, flagged: false, bottom: 0 },
  { top: 1, flagged: false, bottom: 1 },
  { top: 1, flagged: true, bottom: 1 },
  { top: 3, flagged: false, bottom: 3 },
];

function ImpactDots({ top = 0, flagged = false, bottom = 0 }) {
  const topColor = flagged ? 'bg-destructive' : 'bg-primary';
  return (
    <div className="grid grid-cols-3 gap-1" aria-hidden="true">
      {Array.from({ length: 3 }, (_, i) => (
        <span
          key={`top-${i}`}
          className={cn('impact-dot', i < top ? topColor : 'bg-muted')}
        />
      ))}
      {Array.from({ length: 3 }, (_, i) => (
        <span
          key={`bottom-${i}`}
          className={cn('impact-dot', i < bottom ? 'bg-foreground' : 'bg-muted')}
        />
      ))}
    </div>
  );
}

function OverviewTable({ search = '' }) {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Drawer state lives here since this component owns the row data. Both entry
  // points (row double-click, far-right expand icon) funnel through openDrawer.
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Medical Records drawer state — opened from the Medical Records column icon.
  const [medRecordsReferral, setMedRecordsReferral] = useState(null);
  const [medRecordsOpen, setMedRecordsOpen] = useState(false);
  // SL-disposition modal state. Editing the SL fields/last comment is gated to
  // SmartLight employees — non-SL users see plain read-only text.
  const [slModalOpen, setSlModalOpen] = useState(false);
  const [slModalRow, setSlModalRow] = useState(null);
  const [slModalField, setSlModalField] = useState(null);

  // Backend filter set derived from the shared CE filter selections (model
  // type + HCC pills and the column-header filters below).
  const apiFilters = useCarrierEngagementApiFilters();

  // Column-header filter selections + setter live in the shared CE context so
  // the dropdowns and the data query share one source of truth.
  const { columnFilters, setColumnFilter } = useCarrierEngagementFilters();

  // Single-select option value-sets for the column dropdowns. Loaded once on
  // mount — independent of pagination and any applied filter.
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);

  // Multi-column sort state — an ordered list of { field, direction }. Array
  // order is sort priority (first = primary). No cap on simultaneous columns,
  // mirroring the Carrier Portal table. `openSortMenu` is the column key whose
  // sort dropdown is open, or null.
  const [sortColumns, setSortColumns] = useState([]);
  const [openSortMenu, setOpenSortMenu] = useState(null);

  // ?ordering= value: '-' prefix for descending, comma-joined in priority
  // order. Undefined when nothing is sorted (server then uses its default).
  const ordering = sortColumns.length
    ? sortColumns
      .map(({ field, direction }) => (direction === 'desc' ? `-${field}` : field))
      .join(',')
    : undefined;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const opts = await fetchEngagementFilterOptions();
        if (!cancelled) setFilterOptions(opts || EMPTY_FILTER_OPTIONS);
      } catch {
        // Non-fatal: the table still loads; affected dropdowns just show only
        // the "All" entry. Mirrors CP's tolerant filter-options handling.
        if (!cancelled) setFilterOptions(EMPTY_FILTER_OPTIONS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Monotonic request id: each load captures its id, and only the latest load
  // is allowed to commit its result. This makes rapid sequential filter clicks
  // last-write-wins — an earlier, slower response can't clobber a newer one,
  // and no click is dropped just because a prior query is still in flight.
  const requestIdRef = useRef(0);

  const canEditSl = isSmartLightEmployee();

  const openDrawer = useCallback((row) => {
    setSelectedReferral(row);
    setDrawerOpen(true);
  }, []);

  const openMedRecords = useCallback((row) => {
    setMedRecordsReferral(row);
    setMedRecordsOpen(true);
  }, []);

  const openSlModal = useCallback((row, field) => {
    setSlModalRow(row);
    setSlModalField(field);
    setSlModalOpen(true);
  }, []);

  // Set/replace the sort for a column. Clicking a direction moves the column
  // to the end of the list (lowest priority), matching the Carrier Portal
  // table's behavior.
  const handleSort = useCallback((field, direction) => {
    setOpenSortMenu(null);
    setSortColumns((prev) => [
      ...prev.filter((s) => s.field !== field),
      { field, direction },
    ]);
  }, []);

  const handleClearColumnSort = useCallback((field) => {
    setOpenSortMenu(null);
    setSortColumns((prev) => prev.filter((s) => s.field !== field));
  }, []);

  const getSortIcon = (columnKey) => {
    const activeSort = sortColumns.find((s) => s.field === columnKey);
    if (!activeSort) {
      return <TriangleBoth className="h-3 w-3 opacity-[0.35]" />;
    }
    return activeSort.direction === 'asc' ? (
      <TriangleUp className="h-3 w-3" />
    ) : (
      <TriangleDown className="h-3 w-3" />
    );
  };

  // Update a single row in place after an SL disposition is posted — mirrors
  // CP's patchReferral. No full table reload.
  const patchRow = useCallback((id, patch) => {
    if (!id || !patch) return;
    if (apiFilters.has_new_comments && patch.has_new_comment === false) {
      setRows((prev) => prev.filter((r) => r.referral_id !== id));
      return;
    }
    setRows((prev) => prev.map((r) => (r.referral_id === id ? { ...r, ...patch } : r)));
    // Keep the open drawer's referral in sync if it's the same row.
    setSelectedReferral((prev) => (prev && prev.referral_id === id ? { ...prev, ...patch } : prev));
  }, [apiFilters.has_new_comments]);

  const loadFirstPage = useCallback(async () => {
    const requestId = (requestIdRef.current += 1);
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEngagementReferrals({ search, ordering, filters: apiFilters });
      if (requestId !== requestIdRef.current) return; // superseded — drop it
      setRows(data.results || []);
      setTotalRecords(data.total_records || 0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err?.message || 'Failed to load referrals');
      setRows([]);
    } finally {
      // Only the latest request controls the spinner, so an outpaced response
      // resolving late can't flip loading off while a newer load is running.
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [search, ordering, apiFilters]);

  // Refetches whenever the filter selection changes (apiFilters is memoized in
  // the context, so this only fires on a real change, not every render).
  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  // +2 for the trailing Medical Records and expand-icon columns.
  const colSpan = OVERVIEW_COLUMNS.length + 2;

  return (
    <>
      <div className="overflow-auto flex-1 min-h-0" data-testid="overview-table-scroll">
        <Table className="min-w-[1800px]" wrapperClassName="h-full">
          <TableHeader>
            <TableRow className="bg-muted">
              {OVERVIEW_COLUMNS.map((column) => {
                const filterActive =
                  column.filterable && column.filterKey in columnFilters;
                const sortActive = sortColumns.some((s) => s.field === column.key);
                return (
                  <TableHead
                    key={column.key}
                    data-testid={`overview-col-${column.key}`}
                    className={cn(
                      'h-8 px-3 font-semibold text-xs whitespace-nowrap align-middle transition-colors relative',
                      filterActive || sortActive
                        ? 'bg-filter-bg-active text-filter-text-active'
                        : 'text-foreground'
                    )}
                  >
                    {column.filterable || column.sortable ? (
                      <div className="flex items-center gap-1.5">
                        <span>{column.label}</span>
                        {column.filterable && (
                          <ColumnFilterDropdown
                            options={column.staticOptions || filterOptions[column.optionsKey] || []}
                            value={columnFilters[column.filterKey] ?? ''}
                            onChange={(val) => setColumnFilter(column.filterKey, val)}
                            active={filterActive}
                          />
                        )}
                        {column.sortable && (
                          <>
                            <button
                              type="button"
                              data-testid={`overview-sort-${column.key}`}
                              className="cursor-pointer hover:bg-muted-foreground/10 rounded py-1 px-0.5 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenSortMenu(openSortMenu === column.key ? null : column.key);
                              }}
                            >
                              {getSortIcon(column.key)}
                            </button>
                            {openSortMenu === column.key && (() => {
                              const activeSort = sortColumns.find((s) => s.field === column.key);
                              const currentDir = activeSort?.direction;
                              return (
                                <DropdownMenuContent
                                  open={true}
                                  onClose={() => setOpenSortMenu(null)}
                                  align="end"
                                  className="min-w-[140px]"
                                >
                                  <DropdownMenuItem
                                    onClick={() => handleSort(column.key, 'asc')}
                                    className={currentDir === 'asc' ? 'bg-accent text-accent-foreground font-semibold' : ''}
                                  >
                                    <TriangleUp className="h-4 w-4 mr-2" />
                                    Ascending
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleSort(column.key, 'desc')}
                                    className={currentDir === 'desc' ? 'bg-accent text-accent-foreground font-semibold' : ''}
                                  >
                                    <TriangleDown className="h-4 w-4 mr-2" />
                                    Descending
                                  </DropdownMenuItem>
                                  {activeSort && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleClearColumnSort(column.key)}>
                                        <span className="mr-2">×</span>
                                        Clear sort
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    ) : (
                      column.label
                    )}
                  </TableHead>
                );
              })}
              {/* Medical Records column — cell icons TBD. */}
              <TableHead
                data-testid="overview-col-medical_records"
                className="h-8 px-3 font-semibold text-xs whitespace-nowrap align-middle text-foreground text-center"
              >
                Medical Records
              </TableHead>
              {/* Referral Drawer column — its cell icon opens the drawer. */}
              <TableHead
                data-testid="overview-col-referral_drawer"
                className="h-8 px-3 font-semibold text-xs whitespace-nowrap align-middle text-foreground text-center"
              >
                Referral Drawer
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center py-6">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && error && (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center py-6 text-destructive">
                  {error}
                </TableCell>
              </TableRow>
            )}
            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center py-6 text-muted-foreground">
                  No referrals found.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              !error &&
              rows.map((row, index) => (
                <TableRow
                  key={row.referral_id ?? index}
                  data-testid={`overview-row-${row.referral_id}`}
                  onDoubleClick={() => openDrawer(row)}
                  className="hover:bg-muted/30 select-none cursor-pointer"
                >
                  {OVERVIEW_COLUMNS.map((column) => {
                    const fieldName = column.displayKey || column.key;
                    const val = row[fieldName];
                    const content = column.render ? column.render(val, row) : (val ?? '');
                    const editable = canEditSl && SL_EDITABLE_COLUMNS.has(column.key);
                    return (
                      <TableCell
                        key={column.key}
                        data-testid={`cell-${column.key}`}
                        className={cn(
                          'px-3 py-1 text-foreground text-left align-middle',
                          column.textSize || 'text-xs',
                          column.className
                        )}
                      >
                        <div className="flex items-center min-w-0">
                          {editable ? (
                            <button
                              type="button"
                              data-testid={`sl-edit-${column.key}`}
                              onClick={(e) => {
                                // Don't let the click bubble to the row's
                                // onDoubleClick drawer-open handler.
                                e.stopPropagation();
                                openSlModal(row, column.key);
                              }}
                              className="flex items-center justify-between gap-1 w-full min-w-0 text-left hover:text-primary transition-colors"
                              title={column.truncate ? (val ?? '') : undefined}
                            >
                              <span className={cn('min-w-0', column.truncate && 'truncate')}>
                                {content}
                              </span>
                              {/* Affordance from the design: SL fields open the
                                  modal via a chevron; Last Comment via a "+". */}
                              {column.key === 'latest_comment' ? (
                                <Plus className="h-3 w-3 text-muted-foreground shrink-0" />
                              ) : (
                                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                              )}
                            </button>
                          ) : column.truncate ? (
                            <span className="truncate" title={val ?? ''}>
                              {content}
                            </span>
                          ) : (
                            content
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                  {/* Medical Records cell — records icon + placeholder impact dots.
                      Hovering highlights the control and shows a tooltip;
                      clicking opens the MedicalRecordsDrawer. */}
                  <TableCell
                    data-testid={`cell-medical_records-${row.referral_id}`}
                    className="px-3 py-1 align-middle"
                  >
                    <div className="flex justify-center">
                      <Tooltip
                        content="Open medical records"
                        position="bottom"
                        variant="light"
                      >
                        <button
                          type="button"
                          data-testid={`medical-records-${row.referral_id}`}
                          aria-label="Open medical records"
                          onClick={(e) => {
                            e.stopPropagation();
                            openMedRecords(row);
                          }}
                          className="inline-flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-200 transition-colors"
                        >
                          <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
                          <ImpactDots {...IMPACT_DOT_SAMPLES[index % IMPACT_DOT_SAMPLES.length]} />
                        </button>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell className="px-3 py-1 text-center align-middle">
                    <button
                      type="button"
                      data-testid={`overview-expand-${row.referral_id}`}
                      aria-label="Open referral drawer"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDrawer(row);
                      }}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >
                      <PanelRightClose className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {!loading && !error && rows.length > 0 && (
        <div className="sticky bottom-0 z-10 flex items-center justify-center gap-4 border-t border-border bg-accent px-3 py-1">
          <span className="text-xs text-muted-foreground">
            {rows.length} of {totalRecords.toLocaleString()} results
          </span>
          {/* TODO: wire up download */}
          <button
            type="button"
            className="h-6 px-4 text-xs font-semibold flex items-center gap-1.5 text-muted-foreground hover:text-foreground bg-background hover:bg-accent border border-muted-foreground/30 hover:border-foreground rounded transition-colors"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        </div>
      )}

      <EngagementReferralDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        referral={selectedReferral}
        canEditSl={canEditSl}
        onSlPosted={patchRow}
      />

      <MedicalRecordsDrawer
        open={medRecordsOpen}
        onClose={() => setMedRecordsOpen(false)}
        referral={medRecordsReferral}
      />

      {slModalOpen && (
        <SlDispositionModal
          open={slModalOpen}
          onClose={() => setSlModalOpen(false)}
          refId={slModalRow?.referral_id}
          currentResponse={slModalRow?.sl_response ?? null}
          currentReason={slModalRow?.sl_reason ?? null}
          currentAction={slModalRow?.sl_action ?? null}
          initialFocusField={slModalField}
          needsReplacesFetch
          onSuccess={(patch) => patchRow(slModalRow?.referral_id, { ...patch, has_new_comment: false })}
        />
      )}
    </>
  );
}

export { OverviewTable, OVERVIEW_COLUMNS };
